import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import { useDispatch, useSelector } from "react-redux";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";

const PRIMARY = "#286a94";

const ConfirmedInterviews = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const { userResult } = useSelector((state) => state.Auth);
  const candidateId = userResult.userid;

  const getConfirmedInterviews = async () => {
    if (!candidateId) return;
    setLoading(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_PATH}/ebench/candidate/confirmed/interviews`,
        { params: { candidateId } }
      );
      setInterviews(res?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch confirmed interviews", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getConfirmedInterviews();
    dispatch(CandidateProfile(candidateId));
  }, [dispatch, candidateId]);

  const handleViewInterview = (interviewId) => {
    navigate(`/interviews_info/${interviewId}`);
  };

  const formatDuration = (duration) => {
    if (!duration) return "-";

    const [hours, minutes] = duration.split(":").map(Number);

    if (hours > 0 && minutes > 0) {
      return `${hours} hr ${minutes} mins`;
    }

    if (hours > 0) {
      return `${hours} hr`;
    }

    return `${minutes} mins`;
  };

  const formatTime = (time) => {
    if (!time) return "-";

    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;

    return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const isJoinEnabled = (interviewStartTime) => {
    if (!interviewStartTime) return false;

    const startTime = new Date(interviewStartTime).getTime();
    const tenMinutesBefore = startTime - 10 * 60 * 1000;

    return Date.now() >= tenMinutesBefore;
  };

  return (
    <Box className="common_wrapper mt-5" sx={{ px: { xs: 1, sm: 0 } }}>
      {/* Header */}
      <Box mb={3} px={{ xs: 1, sm: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            color: PRIMARY,
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          }}
        >
          Confirmed Interviews
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
        >
          Your confirmed interview schedules
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : interviews.length > 0 ? (
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{
            borderRadius: 3,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{ backgroundColor: "#f2f7fb", whiteSpace: "nowrap" }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  }}
                >
                  Interview
                </TableCell>

                {!isMobile && (
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.7rem", sm: "0.85rem" },
                    }}
                  >
                    Company
                  </TableCell>
                )}

                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  }}
                >
                  Interview Date
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  }}
                >
                  Interview Time
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  }}
                >
                  Interview Duration
                </TableCell>

                {!isMobile && (
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.7rem", sm: "0.85rem" },
                    }}
                  >
                    Interview Round
                  </TableCell>
                )}

                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  }}
                >
                  Interview Mode
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  }}
                >
                  Interview Status
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  }}
                  align="center"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {interviews.map((item) => (
                <TableRow
                  key={item.interviewId}
                  hover
                  sx={{ "&:hover": { backgroundColor: "#f8fbff" } }}
                >
                  <TableCell>
                    <Typography
                      fontWeight={600}
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.interviewTitle}
                    </Typography>
                  </TableCell>

                  {!isMobile && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.companyName}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {item.interviewDate?.split(" ")[0]}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {formatTime(item.interviewStartTime)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {formatDuration(item.duration)}
                    </Typography>
                  </TableCell>

                  {!isMobile && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Round {item.round}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell>
                    <Chip
                      label={item.interviewMode}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        backgroundColor:
                          item.interviewMode === "REMOTE"
                            ? "#e6f0f8"
                            : "#f2f7fb",
                        color: PRIMARY,
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label="CONFIRMED"
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        backgroundColor: "#e6f0f8",
                        color: PRIMARY,
                      }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {item.interviewLink && (
                        <Tooltip
                          title={
                            isJoinEnabled(item.interviewStartTime)
                              ? "Join Interview"
                              : "You can join 10 minutes before the interview starts"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!isJoinEnabled(item.interviewStartTime)}
                              sx={{
                                backgroundColor: "#e6f0f8",
                                color: PRIMARY,
                                p: { xs: 0.75, sm: 1 },
                                "&:hover": { backgroundColor: "#d7e8f5" },
                                "&.Mui-disabled": {
                                  backgroundColor: "#f0f0f0",
                                  color: "#b0b0b0",
                                },
                              }}
                              onClick={() =>
                                window.open(item.interviewLink, "_blank")
                              }
                            >
                              <VideoCallOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: "#e6f0f8",
                            color: PRIMARY,
                            p: { xs: 0.75, sm: 1 },
                            "&:hover": { backgroundColor: "#d7e8f5" },
                          }}
                          onClick={() => handleViewInterview(item.interviewId)}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: PRIMARY,
            border: "1px dashed #cfe3f1",
            borderRadius: 3,
          }}
        >
          <EventAvailableOutlinedIcon sx={{ fontSize: 56 }} />
          <Typography fontWeight={600} mt={1}>
            No confirmed interviews found
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ConfirmedInterviews;
